import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import upperFirst from 'lodash/upperFirst';
import Data from '../data';
import ShipStationModule from '../shipstation';
import Utils from '../utils';

const { ServiceDataHelper, CalculatedData } = Data;
const { ShipStation } = ShipStationModule;
const { Communication, Session, Util: CommonUtil } = Utils;

class Util {
	static _preferences = {};

	static _preferredServicesMap = {};

	/**
	 * Save the new preferences in the Settings tab component after a delay.
	 * All previously unsaved data will be saved once the debounce timer runs out.
	 *
	 * @param {object} newPreferences The new preference object to be stored then saved after a delay.
	 * @param {function} callback Callback to show an error modal if the API call is unsuccessful.
	 */
	static saveNewPreferences = (newPreferences, callback) => {
		this._preferences = isObject(newPreferences)
			? {
					...this._preferences,
					...newPreferences,
				}
			: this._preferences;

		const preferencesParam = !isEmpty(this._preferences)
			? Object.entries(this._preferences).map(([name, value]) => ({
					Name: upperFirst(name),
					Value: value,
				}))
			: [];

		this._callSetPreferenceApi(preferencesParam, callback);
	};

	/**
	 * Save the new Orders preferences in the Settings tab component after a delay.
	 * All previously unsaved data will be saved once the debounce timer runs out.
	 *
	 * @param {string} newPreferences The new shipstation setting to be stored then saved after a delay.
	 * @param {function} callback Callback to show an error modal if the API call is unsuccessful.
	 */
	static saveOrdersPreferences = (newPreferences, callback) => {
		this._callSaveSettingsApi(newPreferences, callback);
	};

	static _callSetPreferenceApi = debounce(
		async (preferencesParam, callback) => {
			const { AllowedTargetOrigin } = Session.getSettings();
			const response = await Session.savePreferences(preferencesParam);

			if (Communication.isSuccessful(response)) {
				// send the user pref data to Ext to update the local account model
				const localUserPrefData = { type: 'UserPref', ...this._preferences };

				this._preferences = {};

				CommonUtil.postMessage(localUserPrefData, AllowedTargetOrigin);

				return;
			}

			if (callback) {
				callback({ response });
			}
		},
		1000
	);

	static _callSaveSettingsApi = debounce(async (newPreferences, callback) => {
		const { AllowedTargetOrigin } = Session.getSettings();

		if (ShipStation.isShipStationConnected()) {
			const response = await ShipStation.saveSettings(newPreferences);

			if (Communication.isSuccessful(response)) {
				const settingsResponse = response?.data?.settings || {};
				const localShipStationData = {
					type: 'ShipStationData',
					...settingsResponse,
				};

				CommonUtil.postMessage(localShipStationData, AllowedTargetOrigin);

				return;
			}

			if (callback) {
				callback({ response });
			}
		}
	}, 1000);

	static translatePreferredServicesToSERA = preferredServices => {
		const seraPreferredServices = Object.keys(preferredServices).reduce(
			(acc, key) => {
				const [serviceType, packageType] = key.split('_');
				const carrier = ServiceDataHelper.getCarrierValue(serviceType);
				const seraServiceType =
					ServiceDataHelper.translateServiceTypeToSERA(serviceType);
				const seraPackageType = ServiceDataHelper.translatePackageTypeToSERA(
					carrier,
					packageType
				);

				// exclude undefined data from translation
				if (!seraPackageType) {
					return acc;
				}

				return {
					...acc,
					[`${seraServiceType}_${seraPackageType}`]: preferredServices[key],
				};
			},
			{}
		);

		return seraPreferredServices;
	};

	static translatePreferredServicesToSWSIM = serviceData =>
		this._preferredServicesMap[serviceData];

	static buildpreferredServicesMap = () => {
		CalculatedData.allServices.forEach(
			({ value, carrier, serviceType, packageType }) => {
				const swsimServiceType =
					ServiceDataHelper.translateServiceTypeToSWSIM(serviceType);
				const swsimPackageType = ServiceDataHelper.translatePackageTypeToSWSIM(
					carrier,
					serviceType,
					packageType
				);

				if (packageType) {
					this._preferredServicesMap[value] =
						`${swsimServiceType}_${swsimPackageType}`;
				}
			}
		);
	};
}

export default Util;
